import { v4 as uuidv4 } from "uuid";
import * as machinemotion from "../api/machinemotion";
import helpers from "../helpers";
import { _uniqueName } from "./utils";

import { Axis } from "./Axis";
import { CalibrationPart } from "./CalibrationPart";
import { Controller } from "./Controller";
import { Input } from "./Input";
import { Notification } from "./Notification";
import { Output } from "./Output";
import { ApplicationTypes } from "./constants/ApplicationTypes";

let gIOMap;


/**
 * @typedef {import("./Application").Application} Application
 * @typedef {import("./CalibrationPart").CalibrationPart} CalibrationPart
 * @typedef {import("./Library").Library} Library
 * @typedef {import("../types").UUID} UUID
 */



/**
 * Add the missing calibration part to the configuration and return true if it was added.
 * The function had some side effect if true was returned.
 *
 * @param {Config} pConfig
 * @param {CalibrationPartData} pCalibrationPart
 * @returns {boolean} true if a CalibrationPart was added to the Config.
 */
const addMissingCalibrationPart = (pConfig, calibrationPart) => {
  const pCalibrationParts = pConfig.mCalibrationParts;
  if ( !pCalibrationParts.some( (cp) => cp.hasSameUUIDOrVID(calibrationPart)) ) {
    const newCalibrationPart = new CalibrationPart(pConfig, {parameters: calibrationPart});
    pCalibrationParts.push(newCalibrationPart);

    return true;
  }

  return false;
};

/**
 * Add the application id to the CalibrationPart and return true if it was added.
 * The function had some side effect if true was returned.
 * @param {Array<CalibrationPart>} pCalibrationParts
 * @param {CalibrationPartData} calibrationPart
 * @param {UUID} appUUID
 * @returns {boolean} True if the calibration part was updated
 */
const addApplicationToCalibrationPart = (pCalibrationParts, calibrationPart, appUUID) => {
  const cp = pCalibrationParts.find( (cp) => {
    return cp.hasSameUUIDOrVID(calibrationPart);
  });

  return cp?.addApplicationUUID(appUUID);
};

/**
 * Add the all the CalibrationPart found in the to the application to the config.
 * The function had some side effect if true was returned.
 * @param {Config}
 * @param {Application} pApp
 * @returns {boolean} True is returned if a new CalibrationPart found in the application was added.
 */
const addCalibrationParts = (pConfig, pApp) => {
  const appCalibrationParts = pApp.robotParameters?.calibrationParts;
  if ( !appCalibrationParts || appCalibrationParts.length === 0 ) {
    return;
  }

  let dirty = false;
  const configCalibrationParts = pConfig.mCalibrationParts;
  for(const calibrationPart of appCalibrationParts) {
    dirty |= addMissingCalibrationPart(pConfig, calibrationPart);
  }

  return !!dirty;
};

/**
 * Add the application to the calibration parts if one if its instruction uses the CalibrationPart.
 * The function had some side effect if true was returned.
 * @param {Config} pConfig
 * @param {Application} pApp
 * @returns {boolean} True is returned if an instruction use a CalibrationPart and the CalibrationPart
 *                    needed to be updated.
 */
const addApplicationIfInstructionUsesCalibrationParts = (pConfig, pApp) => {
  let dirty = false;
  for (const sequence of pApp.mSequences) {
    for (const instruction of sequence.mInstructions) {
      const calibrationReferenceFrame = instruction.getParameter("calibrationReferenceFrame");
      if ( calibrationReferenceFrame ) {
        dirty |= addApplicationToCalibrationPart(pConfig.mCalibrationParts, calibrationReferenceFrame, pApp.mId);
      }
    }
  }

  return !!dirty;
};

/**
 * Represents the configuration for a single controller.
 * @param {ConfigManager} pManager
 * @param {Library} pLibrary
 */
export function Config(pManager, pLibrary, pData) {
  Notification.call(this);

  this.mManager       = pManager;
  this.mLibrary       = pLibrary;
  this.mController    = undefined;
  this.mAxes          = [];
  this.mAxisColors    = this._getAllColors();
  this.mDeviceInfo    = {
    devices : [ 1, 2, 3, 4, 5, 6, 7, 8 ],
    pins    : [ 0, 1, 2, 3 ]
  };
  this.mInputMap      = {};
  this.mInputs        = [];
  this.mOutputMap     = {};
  this.mOutputs       = [];
  this.mDeviceTypeMap = {};
  this.mCompatibleAxisTypesForConfigChecker = []; // ConfigChecker is available in ControlCenter only.
  this.mCalibrationParts = [];

  this.fromJson(pData);
}

Config.prototype = Object.create(Notification.prototype);
Config.prototype.constructor = Config;

Config.prototype.setDirty = function() {
  this.mManager.setDirty();
};

Config.prototype._getAllColors = function() {
  return ["green", "yellow", "red", "blue"];
};

/**
 * @param {import('./ApplicationBase').ApplicationBase} pApp
 * @returns {boolean} True if the application was added to a CalibrationPart
 */
Config.prototype.addApplicationInCalibrationParts = function(pApp) {
  if (!this.getController().isHost() || !this.getController().isParent() ) {
    return false;
  }

  if (pApp.getType() === ApplicationTypes.Python) {
    return false;
  }

  let dirty = addCalibrationParts(this, pApp);

  dirty = addApplicationIfInstructionUsesCalibrationParts(this, pApp) || dirty;

  if ( dirty ) {
    this.setDirty();
  }

  return dirty;
};

/**
 * Remove the application in all the CalibrationParts
 * @param {Application} pApp
 * @returns {boolean} True if there was modifications to the CalibrationParts
 */
Config.prototype.removeApplicationInCalibrationParts = function( pApp ) {
  let dirty = false;
  this.mCalibrationParts = this.mCalibrationParts.map( (cp) => { dirty = cp.removeApplicationUUID(pApp.mId) || dirty; return cp; } );

  this.setDirty();

  return dirty;
};


Config.prototype.updateApplicationInCalibrationPart = function(pApp) {
  let dirty = this.removeApplicationInCalibrationParts(pApp);
  dirty = this.addApplicationInCalibrationParts(pApp) || dirty;

  // TODO: DCT: Remove This when there will be a UI to notify the user of a change
  // in the config when adding an application
  if ( dirty ) {
    this.mManager.deploy( (result) => {
      if(result === null) {
        console.error('failed to deploy configuration');
        return;
      }
    });
  }
};

Config.prototype.updateAxisColors = function() {
  // Update the mAxisColors as per the number of supported drive
  const lNumberOfDrives = this.mController.getNumberOfDrives();
  this.mAxisColors = this._getAllColors().slice(0, lNumberOfDrives);
};

Config.prototype.resetColors = function() {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const lAxisColorUsed = []; let lColorableIndex = 0;
  // Based on the UI order of configuration instructions (mAxes), assign colors to colorable axes.
  for( const pAxis of this.mAxes) {
    if (!pAxis) { return; }

    if (pAxis.isNonMotorized() || (lAxisColorUsed.length >= this.mAxisColors.length)) { return; }
    const lCurrentColor = this.mAxisColors[lColorableIndex];
    lAxisColorUsed.push(lCurrentColor);
    pAxis.setColor(lCurrentColor);
    lColorableIndex++;
  }
};

Config.prototype.getColors = function() { return this.mAxisColors; };

Config.prototype.getName = function() { return 'Configuration'; };

Config.prototype.getCalibrationPartByName = function(name) {
  return this.mCalibrationParts.find( cp => cp.mName === name );
};

Config.prototype.getConfigManager = function() { return this.mManager; };

/**
 * Retrieve the controller.
 *
 * @returns {Controller} Controller associated with this configuration.
 */
Config.prototype.getController = function() { return this.mController; };

/**
 * Sets the controller.
 *
 * @param {Controller} pController Controller to set to.
 */
Config.prototype.setController = function(pController) {
  this.mController = pController;
  this.updateAxisColors();
};

Config.prototype.listRobots = function () {
  return this.mLibrary.robotAppsService.selectors.getRobotConfigArray();
};

Config.prototype.uniqueAxisName = function(pAxisName) {
  return _uniqueName(pAxisName, this.mManager.listAxes().map(function(pAxis) { return pAxis.getName(); }));
};

Config.prototype.uniqueRobotName = function(pAxisName) {
  return _uniqueName(pAxisName, this.mManager.listRobots().map(function(pRobot) { return pRobot.getName(); }));
};

Config.prototype.findUnusedColor = function() {
  // Note: an axis can have multiple colors
  const lAxisList = this.listAxes();
  for (let lColorIdx = 0; lColorIdx < this.mAxisColors.length; lColorIdx++) {
    const lColor = this.mAxisColors[lColorIdx];

    const lAxisIndex = lAxisList.findIndex(function(pAxis) {
      if(pAxis.isNonMotorized()) {
        return false;
      }

      return pAxis.mColor == lColor || pAxis.mChildrenDrives && pAxis.mChildrenDrives.findIndex(function(pChild) { return pChild.color === lColor; }) >= 0;
    });

    if (lAxisIndex == -1) {
      return lColor;
    }
  }

  console.error("Failed to find an unused color.");
  return undefined;
};

Config.prototype.checkForActuatorAddError = function(pActuatorType, pCurrentType = undefined) {
  // Adding a pneumatic actuator
  if(this.isAxisNonMotorized(pActuatorType)) {

    if(this.reachedMaxOutputCombination()) {
      return {'error' : "Max number of outputs are already in use." };
    }

    if (!this.canAddNonMotorizedActuatorOnSameDevice(pActuatorType)) {
      if(pActuatorType === "pneumatic_bag_gripper"){
        return {'error' : "Insufficient number of free inputs or outputs on same device (two inputs and outputs required) to configure a pneumatic actuator on a single digital IO module."};
      }
      else {
        return {'error' : "Insufficient number of free outputs (two required) to configure a pneumatic actuator on a single digital IO module." };
      }
    }

    return undefined;
  }

  // Adding a motor
  if(pCurrentType && !pCurrentType.includes("pneumatic")) { return undefined; } // already a motor

  if(!this.canAddRealAxes()) {
    return {'error' : "Max number of drives are already in use"} ;
  }

  return undefined;
};

Config.prototype.addAxis = function(pAxisData) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)

  // verify that we can add an axis before going further
  const lType = pAxisData.parameters.axisType;
  const _lResult = this.checkForActuatorAddError(lType);
  if(_lResult && _lResult.error) { return _lResult; }

  const _lAddedAxis = new Axis(this, pAxisData);

  if (_lAddedAxis.isNonMotorized()) {
    this.mAxes.push(_lAddedAxis);
    this.updateActuatorIOMap();
  } else {
    this.mAxes.splice(_lAddedAxis.getDriveNumber() - 1, 0, _lAddedAxis);
  }

  this.setDirty();
  this.notify('onAxisAdded', _lAddedAxis);
  return _lAddedAxis;
};

/**
 *
 * @param {object} pDevicePinMap // {0: true, 1: true, 2: false, 3: false}
 * @returns {array} an array of valid pins that are not being used
 */
Config.prototype.getAvailablePins = function(pDevicePinMap) {
  const lKeys = Object.keys(pDevicePinMap).filter(key => pDevicePinMap[key] === false);
  return lKeys;
};

/**
 *
 * @returns {object} {
          device: number,
          pushOutPin: number,
          pullOutPin: number,
          pushInPin: number,
          pullInPin: number
        }
 */
Config.prototype.nextNonMotorizedAxisInputsAndOutputs = function(){

  const lOutputMap = this.mOutputMap;
  const lInputMap = this.mInputMap;
  const lKeys = Object.keys(lOutputMap);
  let lOutput = undefined;

  for(let i = 0; i < lKeys.length; i++){

    // get only available pins for both lOutputMap and lInputMap on the same device
    const lValidOutputPins = this.getAvailablePins(lOutputMap[lKeys[i]]);
    const lValidInputPins = this.getAvailablePins(lInputMap[lKeys[i]]);

    if(lValidOutputPins.length >= 2 && lValidInputPins.length >= 2){
      // both output and input on the same device have enough pins
      // return the device number
      lOutput =  {
        device: parseInt(lKeys[i]),
        pushOutPin: parseInt(lValidOutputPins[0]),
        pullOutPin: parseInt(lValidOutputPins[1]),
        pushInPin: parseInt(lValidInputPins[0]),
        pullInPin: parseInt(lValidInputPins[1]),
      };
      break;
    }
  }

  // handle error in parent when undefined is returned
  return lOutput;
};

/**
 * Find next device and output available
 * only used for adding a pneumatic actuator
 * @returns { { device: number, pushOutPin: number, pullOutPin: number } }
 */
Config.prototype.nextNonMotorizedAxisOutputs = function() {
  const lThis = this;

  for (const lDevice in this.mOutputMap) {
    if (this.mDeviceTypeMap[lDevice] == 'digitalIOModule' || this.mDeviceTypeMap[lDevice] == '?') {
      // get available output for device
      const lAvailablePins = this.getAvailablePins(lThis.mOutputMap[lDevice]);
      if (lAvailablePins.length < 2) { continue; }

      // we will take the first 2, this might not be optimal, but it's simple,
      // NOTE: pushOutPin must be bigger than pullOutPin, otherwise on creation, it will fail due to the way defaults are applied in Config.newAxis
      const lParams = { 'device' : parseInt(lDevice), 'pushOutPin': parseInt(lAvailablePins[0]), 'pullOutPin': parseInt(lAvailablePins[1]) };
      return lParams;
    }
  }

  return undefined;
};

/**
 * Find next device and output pins available for a specific control module
 *
 * @param {!number} - control module number [1,2,3]
 * @returns { { device: number, pushOutPin: number, pullOutPin: number } }
 */
Config.prototype._nextNonMotorizedAxisOutputsByDevice = function(pDevice) {
  let lParams = {};
  const lDeviceMap = this.mOutputMap[pDevice];
  const lAvailablePins = this.getAvailablePins(lDeviceMap);
  if (lAvailablePins.length < 2) {
    lParams = this.nextNonMotorizedAxisOutputs();
  } else {
    lParams.device = pDevice;
    lParams.pushOutPin = parseInt(lAvailablePins[0]);
    lParams.pullOutPin = parseInt(lAvailablePins[1]);
  }

  return lParams;
};

/**
 * Find next device and input available
 * used for adding pneumatic actuators and bag gripper
 * @returns {{ device: number, pushInPin: number, pullInPin: number }}
 */
Config.prototype.nextNonMotorizedAxisInputs = function() {
  let _lParams = undefined;
  const _lThis = this;
  for (const p in _lThis.mInputMap) {
    if (_lParams != undefined && _lParams['pullInPin'] == undefined) {
      // Push and pull pins must be on same device (because of widget)
      _lParams = undefined;
    }
    for (const s in _lThis.mInputMap[p]) {
      if (!_lThis.mInputMap[p][s] && _lParams == undefined) {
        _lParams = { 'device': parseInt(p), 'pushInPin': parseInt(s) };
      }
      if (!_lThis.mInputMap[p][s] && _lParams != undefined && _lParams['pullInPin'] == undefined && parseInt(s) != _lParams['pushInPin']) {
        _lParams['pullInPin'] = parseInt(s);
      }
    }
  }
  return _lParams;
};

/**
 * Find next device and
 * input pins available for a specific control module
 *
 * @param {!number} - control module number [1,2,3]
 * @returns {{ device: number, pushInPin: number, pullInPin: number }}
 */
Config.prototype._nextNonMotorizedAxisInputsByDevice = function(pDevice) {
  let lParams = {};
  const lDeviceMap = this.mInputMap[pDevice];
  const lAvailablePins = Object.keys(lDeviceMap).reduce(function(acc, curr) {
    if (!lDeviceMap[curr]) { acc.push(curr); }
    return acc;
  }, []);

  if (lAvailablePins.length < 2) {
    lParams = this.nextNonMotorizedAxisInputs();
  } else {
    lParams.device = pDevice;
    lParams.pushInPin = parseInt(lAvailablePins[0]);
    lParams.pullInPin = parseInt(lAvailablePins[1]);
  }

  return lParams;
};

/**
 * Construct the IO Map initial state
 * This is memoized with gIOMap
 *
 * @returns {Object} - Map the available devices to the state of the state of their respective pins.
 */
Config.prototype.mapIOInitialState = function() {
  if (gIOMap) { return helpers.deepClone(gIOMap); }
  const lDevices = [ ...this.mDeviceInfo.devices ];
  const lPins = [ ...this.mDeviceInfo.pins ];

  const lMap = lDevices.reduce(function(acc, curr) {
    if (!acc.hasOwnProperty(curr)) {
      acc[curr] = {};
      lPins.forEach(function(pPin) {
        acc[curr][pPin] = false;
      });
    }
    return acc;
  }, {});

  gIOMap = helpers.deepClone(lMap);
  return helpers.deepClone(lMap);
};

Config.prototype.DeviceTypeMapInitialState = function() {
  const lDevices = [ ...this.mDeviceInfo.devices ];
  const lMap = {};
  lDevices.forEach(function(pDevice) {
    lMap[pDevice] = '?';
  });
  return lMap;
};

/**
 * Run through the current configuration
 * and update Input/Output Map
 *
 */
Config.prototype.updateActuatorIOMap = function() {
  const lThis = this;
  const lPneumaticActuators = this.mAxes.filter(function(pAxis) { return pAxis.isNonMotorized(); });
  const lOutputs = this.mOutputs;
  const lInputs = this.mInputs;
  // Reset Output
  this.mOutputMap = this.mapIOInitialState();
  this.mInputMap = this.mapIOInitialState();
  this.mDeviceTypeMap = this.DeviceTypeMapInitialState();

  // Update Pneumatics
  lPneumaticActuators.forEach(function(pPneumaticAxis) {
    const lDevice = pPneumaticAxis.getDevice();
    Object.keys(pPneumaticAxis.mPins).forEach(function(pPinKey) {
      if (/out/i.test(pPinKey)) {
        lThis.mOutputMap[lDevice][pPneumaticAxis.mPins[pPinKey]] = true;
      }
      else {
        if (undefined != pPneumaticAxis.mPins[pPinKey]) {
          lThis.mInputMap[lDevice][pPneumaticAxis.mPins[pPinKey]] = true;
        }
        lThis.mDeviceTypeMap[lDevice] = 'digitalIOModule';
      }
    });
  });
  // Update Output
  lOutputs.forEach(function(pOutput) {
    lThis.mOutputMap[pOutput.getDevice()][pOutput.getPin()] = true;
    lThis.mDeviceTypeMap[pOutput.getDevice()] = pOutput.getDeviceType();
  });

  // Update Input
  lInputs.forEach(function(pInput) {
    // push button only have 2 real pins, we will make the rest unavailable
    if(pInput.getDeviceType() === "pushButtonModule") {
      lThis.mInputMap[pInput.getDevice()][2] = true;
      lThis.mInputMap[pInput.getDevice()][3] = true;
    }
    lThis.mInputMap[pInput.getDevice()][pInput.getPin()] = true;
    lThis.mDeviceTypeMap[pInput.getDevice()] = pInput.getDeviceType();
  });
  this.notify('onIOMapUpdate');
};

Config.prototype._isDeviceEmpty = function(device) {
  return (Object.values(this.mInputMap[device]).every((pin)=> pin == false ) &&
    Object.values(this.mOutputMap[device]).every((pin)=> pin == false ));
};

/**
 * Retrieves the list of Axes.
 *
 * @returns {Array<Axis>} Array of axes.
 */
Config.prototype.listAxes = function() {
  return this.mAxes;
};

Config.prototype.getDefaultNewAxis = function(/*optional*/pType){
  // Note Audrey : pType argument used by assembler's Config.prototype.newAxis
  const lThis = this;
  const lAxisInst = {
    'category' : 'config',
    'name' : 'axis',
    'parameters' : {}
  };
  let lDeviceParams;

  if(this.mLibrary.getInstructionSet()) {
    const lIsPneumaticDesired = pType && pType.includes('pneumatic');
    const lIsPneumatic = lIsPneumaticDesired || !this.canAddRealAxes() ;

    this.mLibrary.getInstructionSet().config.axis.parameters.forEach(function(pParam) {
      if(pParam.name == 'friendlyName')   { lAxisInst.parameters[pParam.name] = lThis.uniqueAxisName('New actuator'); }
      else if(pParam.name == 'axisColor') { lAxisInst.parameters[pParam.name] = !lIsPneumatic ? lThis.findUnusedColor() : '';  /* pneumatics have no color */}
      else if(pParam.name == 'uuid')      { lAxisInst.parameters[pParam.name] = uuidv4(); }
      else                                { lAxisInst.parameters[pParam.name] = pParam.default; }
    });
    // If no more motors are available, use pneumatics
    if (lIsPneumatic) {
      lAxisInst.name = 'pneumaticAxis';
      lAxisInst.parameters.axisType = 'pneumatic';
      lAxisInst.parameters.axisColor = '';
      lAxisInst.parameters.childrenDrives = [];
    }
    // If the new axis is a bag gripper it needs inputs assigned
    if (pType === "pneumatic_bag_gripper") {
      lAxisInst.parameters.axisType = 'pneumatic_bag_gripper';
      lDeviceParams = this.nextNonMotorizedAxisInputsAndOutputs() || { device: null, pushOutPin: null, pullOutPin: null, pushInPin: null, pullInPin: null };
      lAxisInst.parameters.pushInPin = lDeviceParams.pushInPin;
      lAxisInst.parameters.pullInPin = lDeviceParams.pullInPin;
    }
    else {
      lDeviceParams = this.nextNonMotorizedAxisOutputs() || { device: null, pushOutPin: null, pullOutPin: null };
    }
    lAxisInst.parameters.axisType = pType || lAxisInst.parameters.axisType;
    lAxisInst.parameters.device = lDeviceParams.device || lAxisInst.parameters.device;
    // NOTE: this works as long as pushOutPin is always bigger than pullOutPin on creation.
    lAxisInst.parameters.pushOutPin = lDeviceParams.pushOutPin || lAxisInst.parameters.pushOutPin;
    lAxisInst.parameters.pullOutPin = lDeviceParams.pullOutPin || lAxisInst.parameters.pullOutPin;
    lAxisInst.parameters.controllerId = this.mController.getUUID();
    // Must copy the default value because it is a reference type.
    lAxisInst.parameters.childrenDrives = [...lAxisInst.parameters.childrenDrives];
  }

  return lAxisInst;
};

Config.prototype.newAxis = function() {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const lAxisInst = this.getDefaultNewAxis();
  return this.addAxis(lAxisInst);
};

Config.prototype.removeAxis = function(pAxis) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const _lIndex = this.mAxes.indexOf(pAxis);
  if(_lIndex != -1) {
    this.mAxes.splice(_lIndex, 1);
    this.updateActuatorIOMap();
    this.setDirty();
    this.notify('onAxisRemoved', pAxis);
    return true;
  }
  return false;
};

Config.prototype.newRobot = function () {
  this.mLibrary.robotAppsService.send('Configuration/AddRobotButtonClicked', {
    uniqueRobotName: this.uniqueRobotName('New Robot'),
    controllerId: this.getController().getUUID(),
    config: this
  });
};

Config.prototype.removeRobotInControlCenter = function () {
  this.mLibrary.robotAppsService.send('Configuration/RemoveRobotButtonClicked', { config: this });
};

Config.prototype.moveAxis = function(pFrom, pTo) {
  this.mAxes.splice(pTo, 0, this.mAxes.splice(pFrom, 1)[0]);
  this.setDirty();
  this.notify('onAxisMoved', pFrom, pTo);
};

Config.prototype.sortAxes = function() {
  // First sort the axes which have colors
  // Then not motorized axes are ordered by name
  this.mAxes.sort(function(pFirst, pSecond) {
    const lColor1 = pFirst.isNonMotorized() ? null : pFirst.getColor();
    const lColor2 = pSecond.isNonMotorized() ? null : pSecond.getColor();
    return (undefined != lColor1 && undefined != lColor2) ?
      (pFirst.getDriveNumber() - pSecond.getDriveNumber()) :
      (undefined != lColor1 ? -1 :
        (undefined != lColor2 ? 1 :
          pFirst.getName().localeCompare(pSecond.getName())));
  });
  this.notify('onAxesSorted');
};

Config.prototype.getAxisByUuid = function(pUuid) {
  return this.mAxes.find(function(pAxis) { return pAxis.mUUID === pUuid; });
};

/**
 * Get the number of actuators, including the pneumatic type
 * @returns {number}
 */
Config.prototype.numberOfAxes = function() {
  return this.listAxes().length;
};

Config.prototype.numberOfRobots = function() {
  return this.listRobots().length;
};

/**
 * Get the number of actuators, excluding the not motorized type.
 *
 * @returns {Number} Number of real axes
 */
Config.prototype.numberOfRealAxes = function() {
  return this.listAxes()
    .filter(function (pAxis) { return !pAxis.isNonMotorized(); })
    .reduce(function (pAccumulator) {
      return pAccumulator + 1;
    }, 0);
};

/**
 * Get the list of drives numbers assigned to all actuators
 * @returns {Array}
 */
Config.prototype.getOccupiedDrives = function() {
  const axisList = [];
  this.listAxes().forEach( (pAxis) => {
    if (pAxis.isNonMotorized()) { return; }
    axisList.push(...pAxis.listDrives());
  } );
  return axisList;
};

/**
 * Get the number of not motorized actuators
 *
 * @returns {Number} Number of not motorized axes.
 */
Config.prototype.numberOfNonMotorizedActuators = function() {
  return this.listAxes().filter(function(pAxis) { return pAxis.isNonMotorized(); }).length;
};

/**
 * Gets the max number of axes that can be added to a single controller.
 *
 * @returns {Number} Max number of axes per controller.
 */
Config.prototype.maxNumberOfAxes = function() {
  // Returns the maximum number of motor drives
  return this.mAxisColors.length;
};

Config.prototype.maxNumberOfRobots = function () {
  // TODO: allow more than one robot config
  return 1;
};

Config.prototype.canAddRobots = function () {
  // only one robot is allowed for the entire application
  const lConfigList = this.getConfigManager().getConfigurationList();
  const lConfiguredRobots = lConfigList.filter((pConfig) => {
    // TODO adapt to multiple configs
    return this.mLibrary.robotAppsService.selectors.getRobotConfigArray().length;
  });
  if(lConfiguredRobots.length) { return false; }

  if (!this.getController().isHost() || !this.getController().isParent()) { return false; }

  return this.mLibrary.robotAppsService.selectors.getRobotConfigArray().length < this.maxNumberOfRobots();
};

/**
 * Checks whether not not a real axis can be added.
 *
 * @returns {Boolean} True if a real axis can be added, otherwise false.
 */
Config.prototype.canAddRealAxes = function() {
  return this.getOccupiedDrives().length < this.maxNumberOfAxes();
};

Config.prototype.canAddNonMotorizedActuatorOnSameDevice = function(pActuatorType) {
  // tracking by quantity per device
  // outputs and not motorized actuators are tracked individually while sharing the same resource
  if(pActuatorType === "pneumatic_bag_gripper") {
    return this.nextNonMotorizedAxisInputsAndOutputs() != undefined;
  }
  else {
    return this.nextNonMotorizedAxisOutputs() != undefined;
  }
};

Config.prototype.canAddNonMotorizedActuator = function() {
  // IMPORTANT NOTE : canAddNonMotorizedActuator DOES NOT guarantee you can add a pneumatic actuator.
  // Please use canAddNonMotorizedActuatorOnSameDevice to verify this.
  return this.numberOfNonMotorizedActuators() < ((this.maxNumberOfOutputs() - this.mOutputs.length) / 2 | 0);
};

/**
 * Check if the number of axes and not motorized axes was reached
 *
 * @returns {Boolean} True if the max number has been reached, otherwise false.
 */
Config.prototype.isMaxNumberOfAxesReached = function() {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  // IMPORTANT NOTE : isMaxNumberOfAxesReached DOES NOT guarantee you can add a new actuator (because of canAddNonMotorizedActuator).
  if (this.canAddRealAxes()) { return false; }
  if (this.canAddNonMotorizedActuator()) { return false; }
  return true;
};

/**
 *
 * @param {string} pAxisType Predicate to check if an axis is not motorized
 * @returns
 */
Config.prototype.isAxisNonMotorized = function (pAxisType) {
  return /pneumatic/i.test(pAxisType) || /ac_motor_with_vfd/i.test(pAxisType);
};

/**
 * Checks if an switch of axistype is possible
 * @param {object} pAxis The axis object
 * @param {string} pNewType The desire type
 * @returns {Boolean} True if the switch to the new type is possible, otherwise false
 */
Config.prototype.canSwitchAxisType = function (pAxis, pNewType) {
  const lCurrentType = pAxis.getType();
  const requiresOutputs = (pAxisType) => this.isAxisNonMotorized(pAxisType);
  const requiresOutputsAndInputs = (pAxisType) => ["pneumatic_bag_gripper"].includes(pAxisType);

  // Can always switch to the same type
  if (lCurrentType === pNewType) {
    return true;
  }
  // Switching from input/output axis to "real" axis
  if (requiresOutputs(lCurrentType) && !requiresOutputs(pNewType)) {
    return !this.isMaxNumberOfAxesReached();
  }

  // Switching from "real" axis to input/output axis
  if (!requiresOutputs(lCurrentType)) {
    if (requiresOutputsAndInputs(pNewType)) {
      return Boolean(this.nextNonMotorizedAxisInputsAndOutputs());
    } else if (requiresOutputs(pNewType)) {
      return Boolean(this.nextNonMotorizedAxisOutputs());
    }
  }

  // Switching from machine that requires only outputs to machine that requires inputs and outputs
  if (requiresOutputs(lCurrentType) && !requiresOutputsAndInputs(lCurrentType) && requiresOutputsAndInputs(pNewType)) {
    const lPushInPin = pAxis.getPin('pushInPin');
    const lPullInPin = pAxis.getPin('pullInPin');

    // If both inputs are assigned we can just use the same inputs
    if (lPushInPin && lPullInPin) { return true; }
    else if ((lPushInPin && !lPullInPin) || (!lPushInPin && lPullInPin)) {
      // If only 1 input is assigned we must check if another is available on the same device
      const lNumberOfAvailablePins = this.getAvailablePins(this.mInputMap[pAxis.getDevice()]).length;
      if (lNumberOfAvailablePins >= 1) {
        return true;
      }
    }
    // If we need to select both inputs, look for available inputs
    const nextInputs = this.nextNonMotorizedAxisInputs();
    return nextInputs !== undefined && nextInputs["pushInPin"] !== undefined && nextInputs["pullInPin"] != undefined;
  }

  // By default we assume the switch is possible
  return true;
};

/**
 * Retrieves the inputs.
 *
 * @returns {Array<Input>} Array of inputs.
 */
Config.prototype.listInputs = function() {
  return this.mInputs;
};

/*
 * Check if the maximum number of robots was reached
 * @returns {boolean}
 */
Config.prototype.isMaxNumberOfRobotsReached = function () {
  return !this.canAddRobots();
};

/**
 * Retrieves the outputs.
 *
 * @returns {Array<Output>} Array of output.
 */
Config.prototype.listOutputs = function() {
  return this.mOutputs;
};

Config.prototype.getNumberOfPinsOnDeviceType = function(pDeviceType) {
  switch(pDeviceType) {
    case "powerSwitchModule":
      return 1;
    case "pushButtonModule":
      return 2;
    case "digitalIOModule":
    default:
      return 4;
  }
};

Config.prototype.getRobot = function(pName) {
  return this.mLibrary.robotAppsService.selectors.getRobotConfigArray().find(function(pRobot) { return pRobot.getName() == pName; });
};

Config.prototype.getInputByDeviceAndPin = function(pDevice, pPin) {
  return this.mInputs.find(function(pInput) { return pInput.getDevice() == pDevice && pInput.getPin() == pPin; });
};

Config.prototype.getInputByUuid = function(pUuid) {
  return this.mInputs.find(function(pInput) { return pInput.getUUID() === pUuid; });
};

// is only used in the assembler
Config.prototype.numberOfInputs = function() {
  // include the inputs of pneumatics
  const _lMap = this.mInputMap;
  let _lCount = 0;
  Object.keys(_lMap).forEach(function(pDevice) {
    Object.keys(_lMap[pDevice]).forEach(function(pElement) {
      if(_lMap[pDevice][pElement]) { _lCount += 1; }
    });
  });
  return _lCount;

};

Config.prototype._checkDeviceForAvailablePins = function(pDevice, pIsOutput) {
  const lMaxPins = this.getNumberOfPinsOnDeviceType(this.mDeviceTypeMap[pDevice]);
  if (pIsOutput) {
    if (Object.values(this.mOutputMap[pDevice]).filter((val)=>val == true).length != lMaxPins) { return true; }
  } else {
    if (Object.values(this.mInputMap[pDevice]).filter((val)=>val == true).length != lMaxPins) { return true; }
  }
  return false;
};

Config.prototype.reachedMaxInputCombination = function() {
  // Note (Ajay) : As we add new modules, add input modules to the this list
  const lDevicesConfiguredAsInput = ['digitalIOModule', 'pushButtonModule'];
  for (const id in this.mDeviceTypeMap) {
    if (this.mDeviceTypeMap[id] == '?') { return false; }
    else if (lDevicesConfiguredAsInput.includes(this.mDeviceTypeMap[id]) && this._checkDeviceForAvailablePins(id)) { return false; }
  }
  return true;
};

Config.prototype.reachedMaxOutputCombination = function() {
  // Note (Ajay) : As we add new modules, add output modules to the this list
  const lDevicesConfiguredAsOutput = ['digitalIOModule', 'powerSwitchModule'];
  for (const id in this.mDeviceTypeMap) {
    if (this.mDeviceTypeMap[id] == '?') { return false;}
    else if (lDevicesConfiguredAsOutput.includes(this.mDeviceTypeMap[id]) && this._checkDeviceForAvailablePins(id, true)) { return false; }
  }
  return true;
};

Config.prototype.uniqueInputName = function(pName) {
  return _uniqueName(pName, this.mManager.listInputs().map(function(pInput) { return pInput.getName(); }));
};

Config.prototype.addInput = function(pInputData) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  if(!this.reachedMaxInputCombination()) {
    const _lAddedInput = new Input(this, pInputData);

    this.mInputs.push(_lAddedInput);
    this.updateActuatorIOMap();
    this.setDirty();
    this.notify('onInputAdded', _lAddedInput);
    return _lAddedInput;
  }
};

/**
 *
 * @param {pCalibrationPartData} pCalibrationPartData
 * @returns {CalibrationPart}
 */
Config.prototype.addCalibrationPart = function(pCalibrationPartData) {
  // TODO: Override this function in the assembler
  // WARNING : This function is will be overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const addedCalibrationPart = new CalibrationPart(this, pCalibrationPartData);
  this.mCalibrationParts.push(addedCalibrationPart);
  this.setDirty();
  this.notify('onCalibrationPartAdded', addedCalibrationPart);
  return addedCalibrationPart;
};

Config.prototype._getNextInputParametersByDeviceType = function(pDeviceType) {
  const lMaxPins = this.getNumberOfPinsOnDeviceType(pDeviceType);
  const _lThis = this;
  for(const p in _lThis.mDeviceTypeMap) {
    if (_lThis.mDeviceTypeMap[p] == pDeviceType || _lThis.mDeviceTypeMap[p] == '?') {
      for(const s in _lThis.mInputMap[p]) {
        if(!_lThis.mInputMap[p][s] && s < lMaxPins) {
          return { 'device': parseInt(p), 'pin': parseInt(s) };
        }
      }
    }
  }
  return false;
};

Config.prototype.newInput = function() {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const lThis = this;

  const getAvailableInputType = function() {
    let lType = 'digitalIOModule';
    // If a device port is still unconfigured, use default
    if (Object.values(lThis.mDeviceTypeMap).includes('?')) { return lType; }
    // All ports are configured as a recognized deviceType
    // Check if there are any digitalIOModules with empty pins
    for (const id in lThis.mDeviceTypeMap) {
      if (lThis.mDeviceTypeMap[id] == 'digitalIOModule') {
        if (lThis._checkDeviceForAvailablePins(id)) { return lType; }
      }
    }
    // All digitalIOModules inputs are filled, update default to pushButtonModule
    lType = 'pushButtonModule';
    return lType;
  };

  const lInputType = getAvailableInputType();

  const _lInst = {
    'category' : 'config',
    'name' : 'alias',
    'parameters' : {
      'uuid': uuidv4(),
      'aliasName': this.uniqueInputName('New input'),
      'controllerId': this.mController.getUUID(),
      'category': 'input',
      'deviceType': lInputType,
      'parameters': this._getNextInputParametersByDeviceType(lInputType),
    }
  };
  if(_lInst.parameters.parameters) {
    return this.addInput(_lInst);
  }
};

Config.prototype.removeInput = function(pInput) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const _lIndex = this.mInputs.indexOf(pInput);
  if(_lIndex != -1) {
    const _lRemoved = this.mInputs[_lIndex];
    this.mInputs.splice(_lIndex, 1);
    this.updateActuatorIOMap();
    this.setDirty();
    this.notify('onInputRemoved', _lRemoved);
    return true;
  }
  return false;
};

Config.prototype.moveInput = function(pFrom, pTo) {
  if(pFrom >= 0 && pFrom < this.mInputs.length && pTo >= 0 && pTo < this.mInputs.length) {
    this.mInputs.splice(pTo, 0, this.mInputs.splice(pFrom, 1)[0]);
    this.setDirty();
    this.notify('onInputMoved', pFrom, pTo);
  }
};

Config.prototype.getOutputByDeviceAndPin = function(pDevice, pPin) {
  return this.mOutputs.find(function(pOutput) { return pOutput.getDevice() == pDevice && pOutput.getPin() == pPin; });
};

Config.prototype.getOutputByUuid = function(pUuid) {
  return this.mOutputs.find(function(pOutput) { return pOutput.getUUID() === pUuid; });
};

Config.prototype.numberOfOutputs = function() {
  return this.mOutputs.length + this.numberOfNonMotorizedActuators() * 2;
};

Config.prototype.maxNumberOfOutputs = function() {
  const _lMap = this.mOutputMap;
  let _lCount = 0;
  Object.keys(_lMap).forEach(function(pDevice) {
    Object.keys(_lMap[pDevice]).forEach(function() {
      _lCount += 1;
    });
  });
  return _lCount;
};

Config.prototype.maxNumberOfInputs = function() {
  const lInputMap = this.mInputMap;
  const lCount = Object.keys(lInputMap).reduce((prev, curr) => {
    return prev += Object.keys(lInputMap[curr]).length;
  }, 0);

  return lCount;
};

Config.prototype.uniqueOutputName = function(pName) {
  return _uniqueName(pName, this.mManager.listOutputs().map(function(pOutput) { return pOutput.getName(); }));
};

Config.prototype.canAddInputOfDeviceType = function(pDeviceType) {
  const lThis = this;
  const lInputMap = this.mInputMap;
  const lHasFreePins = Object.keys(lInputMap).some(pDevice => Object.keys(lInputMap[pDevice]).some(pPin => lInputMap[pDevice][pPin] === false));
  if(!lHasFreePins) { return false; } // no free pins

  const lAvailableDevices = [];
  const lDeviceTypeMap = this.mDeviceTypeMap;

  for(const lDevice in lDeviceTypeMap) {
    if(lDeviceTypeMap[lDevice] === "?") { return true; } // Empty device
    if(lDeviceTypeMap[lDevice] === pDeviceType) {
      lAvailableDevices.push(lDevice);
    }
  }

  let canAddInput = false;

  switch(pDeviceType) {
    case "digitalIOModule":
      canAddInput = lAvailableDevices.some(pDeviceNumber => {
        return Object.values(this.mInputMap[pDeviceNumber]).includes(false);
      });
      break;
    case "pushButtonModule":
      canAddInput = lAvailableDevices.some(pDeviceNumber => {
        const lAssignedPins = Object.values(lThis.mInputMap[pDeviceNumber]).filter(pPinBool => pPinBool === true);
        return lAssignedPins.length < 2;
      });
      break;
    default:
      console.error('[Config.canAddInputOfDeviceType] Unknown device type: ' + pDeviceType);
      break;
  }

  return canAddInput;
};

Config.prototype.canAddIoInput = function() {
  return this.canAddInputOfDeviceType('digitalIOModule');
};

Config.prototype.canAddPushButtonInput = function() {
  return this.canAddInputOfDeviceType('pushButtonModule');
};

Config.prototype.canChangeInputToDeviceType = function(pInput, pTargetDeviceType) {
  if(this.canAddInputOfDeviceType(pTargetDeviceType)) { return true; }

  const lCurrentDeviceType = pInput.getDeviceType();
  const lCurrentDevice = pInput.getDevice();
  const lCurrentPin = pInput.getPin();
  const lInputMap = JSON.parse(JSON.stringify(this.mInputMap));
  lInputMap[lCurrentDevice][lCurrentPin] = false;

  switch(pTargetDeviceType) {
    case "pushButtonModule": {
      return Object.values(lInputMap[lCurrentDevice]).every(pin => pin === false);
    }
    case "digitalIOModule": {
      if(lCurrentDeviceType === 'pushButtonModule') {
        if(Object.values(lInputMap[lCurrentDevice]).includes(true)) { return false; }
      }
      return true;
    }
    default: {
      return true;
    }
  }
};

// will check if the input passed as a param can be changed to a pushbutton
// we do this because we have to ignore the pin the input is using
Config.prototype.canChangeToPushButton = function(pInput) {
  return this.canChangeInputToDeviceType(pInput, 'pushButtonModule');
};

// will check if the input passed as a param can be changed to a digital IO module.
Config.prototype.canChangeToDigitalIo = function(pInput) {
  return this.canChangeInputToDeviceType(pInput, 'digitalIOModule');
};

Config.prototype.canAddOutputOfDeviceType = function(pDeviceType) {
  let canAddOutput = false;

  switch(pDeviceType) {
    case "powerSwitchModule":
      canAddOutput = Object.values(this.mDeviceTypeMap).includes("?");
      break;
    default:
      console.error('[Config.canAddInputOfDeviceType] Unknown device type: ' + pDeviceType);
      break;
  }

  return canAddOutput;
};

Config.prototype.canAddPowerSwitchOutput = function() {
  return this.canAddOutputOfDeviceType('powerSwitchModule'); // there must be a completely empty device to add a powerswitch
};

Config.prototype.canChangeOutputToDeviceType = function(pOutput, pTargetDeviceType) {
  if(this.canAddOutputOfDeviceType(pTargetDeviceType)) { return true; }
  if(pOutput.getDeviceType() === pTargetDeviceType) { return true; }

  const lCopyOfOutputMap = JSON.parse(JSON.stringify(this.mOutputMap));
  const lInputMap = this.mInputMap;
  const lCurrentDevice = pOutput.getDevice();
  const lCurrentPin = pOutput.getPin();

  lCopyOfOutputMap[lCurrentDevice][lCurrentPin] = false;

  switch(pTargetDeviceType) {
    case "powerSwitchModule":
      if(Object.values(lCopyOfOutputMap[lCurrentDevice]).includes(true)) { return false; }
      if(Object.values(lInputMap[lCurrentDevice]).includes(true)) { return false; }
      return true;
    default:
      return true;
  }
};

Config.prototype.canChangeToPowerSwitchOutput = function(pOutput) {
  return this.canChangeOutputToDeviceType(pOutput, "powerSwitchModule");
};

Config.prototype.addOutput = function(pOutputData) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  if(!this.reachedMaxOutputCombination()) {
    const _lAddedOutput = new Output(this, pOutputData);

    this.mOutputs.push(_lAddedOutput);
    this.updateActuatorIOMap();
    this.setDirty();
    this.notify('onOutputAdded', _lAddedOutput);
    return _lAddedOutput;
  }
};

Config.prototype._getNextOutputParametersByDeviceType = function(pDeviceType) {
  const lMaxPins = this.getNumberOfPinsOnDeviceType(pDeviceType);
  const _lThis = this;
  for(const p in _lThis.mDeviceTypeMap) {
    if (_lThis.mDeviceTypeMap[p] == pDeviceType || _lThis.mDeviceTypeMap[p] == '?') {
      for(const s in _lThis.mOutputMap[p]) {
        if(!_lThis.mOutputMap[p][s] && s < lMaxPins) {
          return { 'device': parseInt(p), 'pin': parseInt(s) };
        }
      }
    }
  }
  return false;
};


Config.prototype.newOutput = function() {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const _lThis = this;

  const getAvailableOutputType = function() {
    let lType = 'digitalIOModule';
    // If a device port is still unconfigured, use default
    if (Object.values(_lThis.mDeviceTypeMap).includes('?')) {return lType; }
    // All ports are configured as a recognized deviceType
    // Check if there are any digitalIOModules with empty pins
    for (const id in _lThis.mDeviceTypeMap) {
      if (_lThis.mDeviceTypeMap[id] == 'digitalIOModule') {
        if (_lThis._checkDeviceForAvailablePins(id, true)) { return lType; }
      }
    }
    // All digitalIOModules inputs are filled, update default to powerSwitchModule
    lType = 'powerSwitchModule';
    return lType;
  };

  const _lOutputType = getAvailableOutputType();

  const _lInst = {
    'category' : 'config',
    'name' : 'alias',
    'parameters' : {
      'uuid': uuidv4(),
      'aliasName': this.uniqueOutputName('New output'),
      'controllerId': this.mController.getUUID(),
      'category': 'output',
      'deviceType': _lOutputType,
      'parameters': this._getNextOutputParametersByDeviceType(_lOutputType)
    }
  };

  if(_lInst.parameters.parameters) {
    return this.addOutput(_lInst);
  }
};

Config.prototype.removeOutput = function(pOutput) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const _lIndex = this.mOutputs.indexOf(pOutput);
  if(_lIndex != -1) {
    const _lRemoved = this.mOutputs[_lIndex];
    this.mOutputs.splice(_lIndex, 1);
    this.updateActuatorIOMap();
    this.setDirty();
    this.notify('onOutputRemoved', _lRemoved);
    return true;
  }
  return false;
};

Config.prototype.moveOutput = function(pFrom, pTo) {
  if(pFrom >= 0 && pFrom < this.mOutputs.length && pTo >= 0 && pTo < this.mOutputs.length) {
    this.mOutputs.splice(pTo, 0, this.mOutputs.splice(pFrom, 1)[0]);
    this.setDirty();
    this.notify('onOutputMoved', pFrom, pTo);
  }
};

/**
 * Inflate the configuration from a list of instructions.
 *
 * @param {Object[]} pInstructionList
 * @returns
 */
Config.prototype.fromJson = function(pInstructionList) {
  const lThis     = this;

  // Initialize first to populate the IO maps.
  this.initialize();

  // Controller
  if (pInstructionList) {
    pInstructionList.forEach(function(pInst) {
      lThis.addInstructionFromJson(pInst);
    });
  }

  if (!this.mController) {
    // Always ensure that the configuration has a controller.
    this.addController(this);
  }

  this.sortAxes();
};

/**
 * Gets overwritten in the CAD. Controller is replaced with Controller3D
 * @param {*} pData
 * @param {*} pParams
 */
Config.prototype.addController = function(pData, pParams) {
  this.mController = new Controller(pData, pParams);
  this.updateAxisColors();
};

/**
 * Adds a single instruction to the configuration given its JSON.
 * @param {Object} pInst JSON for the instruction.
 *
 * @returns {Boolean} Whether or not the instruction was added.
 */
Config.prototype.addInstructionFromJson = function(pInst) {
  if (pInst.category !== 'config') {
    console.error('Attempting to add an instruction that is not a config type', pInst);
    return false;
  }

  switch (pInst.name) {
    case 'controller': {
      if (this.mController) {
        console.error('Attempting to initialize the same controller twice.', pInst);
        return false;
      }
      this.addController(this, pInst.parameters);
      return true;
    }
    case 'axis':
    case 'pneumaticAxis': {
      this.addAxis(pInst);
      return true;
    }
    case 'robot': {
      this.mLibrary.robotAppsService.send('Configuration/fromJson/addRobotConfigFromInstruction', {
        robotConfig: pInst,
        config: this
      });

      return true;
    }
    case 'alias': {
      if(pInst.parameters) {
        if(pInst.parameters.category == 'input') {
          this.addInput(pInst);
        } else if(pInst.parameters.category == 'output') {
          this.addOutput(pInst);
        }
        return true;
      }

      return false;
    }
    case 'calibrationPart': {
      this.addCalibrationPart(pInst);
      return true;
    }
    default:
      console.error('Unknown name for instruction, name=' + pInst.name, pInst);
      return false;
  }
};

/**
 * Generates a list of instructions that will be included in the startup application.
 *
 * @returns {Object[]} JSONified Application
 */
Config.prototype.toJson = function() {
  let lInstructionList = [];

  // Controller instructions
  lInstructionList.push(this.mController.toJson());

  // Axes instructions
  this.listAxes().forEach(function(pAxis) {
    const _lNextInst = pAxis.toJson();
    if(_lNextInst) {
      lInstructionList = lInstructionList.concat(_lNextInst);
    }
  });

  for ( const calibrationPart of this.mCalibrationParts) {
    lInstructionList.push(calibrationPart.toJson());
  }

  this.mInputs.forEach(function(pInput) {
    lInstructionList.push(pInput.toJson());
  });
  this.mOutputs.forEach(function(pOutput) {
    lInstructionList.push(pOutput.toJson());
  });

  return lInstructionList;
};

Config.prototype.reset = function() {
  this.mController = undefined;
  this.mAxes = [];
  this.mInputMap = {};
  this.mInputs = [];
  this.mOutputMap = {};
  this.mOutputs = [];
  this.mDeviceTypeMap = {};
  this.mCompatibleAxisTypesForConfigChecker = []; // ConfigChecker is available in ControlCenter only.
  this.setDirty();
  this.notify('onReset');
};

Config.prototype.initialize = function() {
  this.reset();

  this.mOutputMap = this.mapIOInitialState();
  this.mInputMap = this.mapIOInitialState();
  this.mDeviceTypeMap = this.DeviceTypeMapInitialState();
};

Config.prototype.getCompatibleAxisTypesForConfigChecker = function() {
  return this.mCompatibleAxisTypesForConfigChecker;
};

Config.prototype.initializeCompatibleAxisTypesForConfigChecker = function(pCompletion) {
  machinemotion.getCompatibleAxisTypes((pResponse) => {
    if (pResponse && pResponse.compatibleAxisTypes) {
      this.mCompatibleAxisTypesForConfigChecker = pResponse.compatibleAxisTypes;
    }
    pCompletion();
  });
};

/**
 * Find if the device and pin are used by another
 * input/output/pneumatic axis
 * @param {!number} pDevice Control module number
 * @param {!number} pPin Pin Number (0-3)
 * @param {!ml.Input|ml.Output|ml.Axis} pObject It can be an Input/Output/Axis Machine Logic object
 * @returns {boolean}
 */
Config.prototype._isDeviceAndPinUsed = function(pDevice, pPin, pObject) {
  if (undefined == pPin) { return false; }
  let lIsUsed = false; const checkPins = function(pPinValue) { if (pPinValue == pPin) { lIsUsed = true; } };
  const checkInputOutput = function(_pIO) {
    // if pObject is input or output we
    // don't check device and pin of different type
    if (/(output|input)/i.test(pObject.constructor.name)) {
      // validate type
      if (_pIO.constructor.name != pObject.constructor.name) { return; }
      if ((_pIO.getName() != pObject.getName()) && (_pIO.getDevice() == pDevice) && (_pIO.getPin() == pPin)) {
        lIsUsed = true;
      }
      // TODO: find a better way to compare type
      // don't trust much the constructor object.
    } else if (/axis/i.test(pObject.constructor.name)) {
      if (_pIO.getDevice() == pObject.getDevice()) {
        Object.values(pObject.mPins).forEach(checkPins);
      }
    }
  };
  const checkAxes = function(_pAxis) {
    if (!_pAxis.isNonMotorized()) { return; }
    // check an device against input/output
    if (/(output|input)/i.test(pObject.constructor.name)) {
      if ((_pAxis.getName() != pObject.getName()) && (_pAxis.getDevice() == pDevice)) {
        Object.values(_pAxis.mPins).forEach(checkPins);
      }
    } else {
      if ((_pAxis.getName() != pObject.getName()) && (_pAxis.getDevice() == pDevice)) {
        Object.values(_pAxis.mPins).forEach(function(pPinValue) {
          Object.values(pObject.mPins).forEach(function(pOtherAxisPinValue) {
            if ((undefined != pPinValue && undefined != pOtherAxisPinValue) && !lIsUsed && (pPinValue == pOtherAxisPinValue)) {
              lIsUsed = true;
            }
          });
        });
      }
    }
  };

  this.mInputs.forEach(checkInputOutput);
  this.mOutputs.forEach(checkInputOutput);

  this.mAxes.forEach(checkAxes);

  return lIsUsed;
};

/**
 * Check if a pin for a
 * specific device is in use
 *
 * @param {!number} pDevice - control module, i.e, device [1,2,3]
 * @param {!number} pPin - pin number [0,1,2,3]
 * @param {!string} pType - Input or output type - ['output', 'input']
 * @returns {Boolean} - true if the pin is already in use
 */
Config.prototype._isPinUsed = function(pDevice, pPin, pType) {
  let lIsPinUsed;

  if (/output/i.test(pType)) {
    lIsPinUsed = this.mOutputMap[pDevice][pPin];
  } else if (/input/i.test(pType)) {
    lIsPinUsed = this.mInputMap[pDevice][pPin];
  }

  return lIsPinUsed;
};
